/** Seed data for the Procurement Products "procurement lens" view (Demo Mode only) - a
 * searchable/filterable table of supplier products (product code, name, supplier, catalogue,
 * category, pack size, UOMs, price, availability, approval status, data quality status and
 * recipe usage). This is a brand-new concept with no real backend endpoint yet (no supplier
 * product feed across catalogues exists), so it is fully mocked and isolated from the real,
 * backend-driven Products list - see src/pages/Procurement/Products/index.tsx.
 *
 * lazy: seed list is small and static (no pagination/sorting server-side) - fine for a demo
 * dataset of this size. Upgrade path if this becomes real: replace with a typed API client
 * under src/app/services/api/procurementProducts/ following the MSW mock-client pattern. */

export enum ProcurementAvailabilityStatus {
  InStock = 'InStock',
  LimitedStock = 'LimitedStock',
  OutOfStock = 'OutOfStock',
  Discontinued = 'Discontinued',
}

export enum ProcurementApprovalStatus {
  Approved = 'Approved',
  PendingApproval = 'PendingApproval',
  UnderReview = 'UnderReview',
  Rejected = 'Rejected',
}

export enum ProcurementDataQualityStatus {
  Complete = 'Complete',
  NeedsReview = 'NeedsReview',
  MissingData = 'MissingData',
}

export enum ProcurementRecipeUsage {
  UsedInRecipes = 'UsedInRecipes',
  NotUsedInRecipes = 'NotUsedInRecipes',
}

export interface ProcurementLensProduct {
  productCode: string;
  productName: string;
  supplierName: string;
  catalogueName: string;
  category: string;
  packSize: string;
  orderableUom: string;
  baseUom: string;
  price: number;
  availability: ProcurementAvailabilityStatus;
  approvalStatus: ProcurementApprovalStatus;
  dataQualityStatus: ProcurementDataQualityStatus;
  recipeUsage: ProcurementRecipeUsage;
  recipeUsageCount: number;
}

export const PROCUREMENT_SUPPLIERS = ['Millbrook Foods', 'Coastal Seafoods', 'Green Valley Produce', 'Northern Dairy Co.'];
export const PROCUREMENT_CATALOGUES = ['National Core Range', 'Regional Fresh', 'Seasonal Specials'];
export const PROCUREMENT_CATEGORIES = ['Bakery', 'Dairy', 'Seafood', 'Produce', 'Dry Goods'];

export const procurementLensProducts: ProcurementLensProduct[] = [
  {
    productCode: 'FLR-001', productName: 'Strong White Flour 16kg', supplierName: 'Millbrook Foods', catalogueName: 'National Core Range',
    category: 'Bakery', packSize: '1 x 16kg', orderableUom: 'Bag', baseUom: 'Kg', price: 12.4,
    availability: ProcurementAvailabilityStatus.InStock, approvalStatus: ProcurementApprovalStatus.Approved,
    dataQualityStatus: ProcurementDataQualityStatus.Complete, recipeUsage: ProcurementRecipeUsage.UsedInRecipes, recipeUsageCount: 6,
  },
  {
    productCode: 'BTR-002', productName: 'Unsalted Butter Block 10kg', supplierName: 'Northern Dairy Co.', catalogueName: 'National Core Range',
    category: 'Dairy', packSize: '1 x 10kg', orderableUom: 'Block', baseUom: 'Kg', price: 38.9,
    availability: ProcurementAvailabilityStatus.InStock, approvalStatus: ProcurementApprovalStatus.PendingApproval,
    dataQualityStatus: ProcurementDataQualityStatus.Complete, recipeUsage: ProcurementRecipeUsage.UsedInRecipes, recipeUsageCount: 11,
  },
  {
    productCode: 'SPC-009', productName: 'Ground Cumin 500g', supplierName: 'Millbrook Foods', catalogueName: 'Regional Fresh',
    category: 'Dry Goods', packSize: '6 x 500g', orderableUom: 'Case', baseUom: 'Gram', price: 22.15,
    availability: ProcurementAvailabilityStatus.OutOfStock, approvalStatus: ProcurementApprovalStatus.Rejected,
    dataQualityStatus: ProcurementDataQualityStatus.MissingData, recipeUsage: ProcurementRecipeUsage.NotUsedInRecipes, recipeUsageCount: 0,
  },
  {
    productCode: 'OLV-002', productName: 'Extra Virgin Olive Oil 5L', supplierName: 'Green Valley Produce', catalogueName: 'Seasonal Specials',
    category: 'Dry Goods', packSize: '4 x 5L', orderableUom: 'Case', baseUom: 'Litre', price: 64.2,
    availability: ProcurementAvailabilityStatus.LimitedStock, approvalStatus: ProcurementApprovalStatus.Approved,
    dataQualityStatus: ProcurementDataQualityStatus.NeedsReview, recipeUsage: ProcurementRecipeUsage.UsedInRecipes, recipeUsageCount: 3,
  },
  {
    productCode: 'CRS-100', productName: 'Fresh Cod Fillet 1kg', supplierName: 'Coastal Seafoods', catalogueName: 'Regional Fresh',
    category: 'Seafood', packSize: '1 x 1kg', orderableUom: 'Each', baseUom: 'Kg', price: 9.75,
    availability: ProcurementAvailabilityStatus.InStock, approvalStatus: ProcurementApprovalStatus.Approved,
    dataQualityStatus: ProcurementDataQualityStatus.Complete, recipeUsage: ProcurementRecipeUsage.UsedInRecipes, recipeUsageCount: 4,
  },
  {
    productCode: 'VEG-014', productName: 'Vine Tomatoes 5kg', supplierName: 'Green Valley Produce', catalogueName: 'Regional Fresh',
    category: 'Produce', packSize: '1 x 5kg', orderableUom: 'Box', baseUom: 'Kg', price: 8.6,
    availability: ProcurementAvailabilityStatus.InStock, approvalStatus: ProcurementApprovalStatus.UnderReview,
    dataQualityStatus: ProcurementDataQualityStatus.NeedsReview, recipeUsage: ProcurementRecipeUsage.NotUsedInRecipes, recipeUsageCount: 0,
  },
  {
    productCode: 'DRY-021', productName: 'Basmati Rice 20kg', supplierName: 'Millbrook Foods', catalogueName: 'National Core Range',
    category: 'Dry Goods', packSize: '1 x 20kg', orderableUom: 'Bag', baseUom: 'Kg', price: 27.5,
    availability: ProcurementAvailabilityStatus.InStock, approvalStatus: ProcurementApprovalStatus.Approved,
    dataQualityStatus: ProcurementDataQualityStatus.Complete, recipeUsage: ProcurementRecipeUsage.UsedInRecipes, recipeUsageCount: 8,
  },
  {
    productCode: 'DAI-033', productName: 'Semi-Skimmed Milk 12x1L', supplierName: 'Northern Dairy Co.', catalogueName: 'Regional Fresh',
    category: 'Dairy', packSize: '12 x 1L', orderableUom: 'Crate', baseUom: 'Litre', price: 10.9,
    availability: ProcurementAvailabilityStatus.Discontinued, approvalStatus: ProcurementApprovalStatus.Rejected,
    dataQualityStatus: ProcurementDataQualityStatus.MissingData, recipeUsage: ProcurementRecipeUsage.NotUsedInRecipes, recipeUsageCount: 0,
  },
];

export function getProcurementLensFilterOptions() {
  return {
    suppliers: PROCUREMENT_SUPPLIERS,
    catalogues: PROCUREMENT_CATALOGUES,
    categories: PROCUREMENT_CATEGORIES,
    availabilities: Object.values(ProcurementAvailabilityStatus),
    approvalStatuses: Object.values(ProcurementApprovalStatus),
    dataQualityStatuses: Object.values(ProcurementDataQualityStatus),
    recipeUsages: Object.values(ProcurementRecipeUsage),
  };
}
